import React, { useState, useEffect, useRef } from 'react';
import LoadingSpinner from '../components/layout/LoadingSpinner';
import { useDispatch, useSelector } from 'react-redux';
import { login as loginAction } from '../features/auth/authSlice';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../components/auth-context/AuthContext';
import { RootState } from '../store';
import { useLocation } from 'react-router-dom';
import { setActiveLink, setActiveIcon } from '../features/sidebar/sidebarSlice';
import { trackEvent } from '../segment';

const ActiveTeachLogin = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const redirectToParam = urlParams.get('redirectTo') || '/book-library';
    const safeRedirectTo = redirectToParam.startsWith('/')
        ? redirectToParam
        : '/book-library';

    const nucleusDomain = process.env
        .REACT_APP_NUCLEUS_ACTIVE_TEACH_URL as string;

    const location = useLocation();
    const logout = location?.state?.logout || false;

    const [isLoading, setIsLoading] = useState(true);
    const iframeRef = useRef<HTMLIFrameElement | null>(null);
    const [retryCount, setRetryCount] = useState(0);
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { login: authLogin } = useAuth();

    const product = useSelector((state: RootState) => state.product);
    const navList = useSelector((state: RootState) => state.SideBarConfig.data);
    const productType = useSelector(
        (state: RootState) => state.SchoolInfo.data.product
    );

    const handleLoad = () => {
        setIsLoading(false);
        handleIframeLoad();
    };

    useEffect(() => {
        // Set up listener for receiving login info from iframe
        const handleLoginMessage = (event: any) => {
            if (event.data.type === 'ACTIVE_TEACH_AUTH_INIT') {
                const receivedData = event.data;
                Object.keys(receivedData).forEach((key) => {
                    localStorage.setItem(key, receivedData[key]);
                });
                let userData = JSON.parse(receivedData.user);
                authLogin(userData, receivedData.token);
                dispatch(
                    loginAction({ user: userData, token: receivedData.token })
                );
                trackEvent('click_login', {});

                // Determine sidebar icon based on role or product
                if (userData.rolecode === 'SCH41') {
                    dispatch(setActiveLink('/book-library'));
                    dispatch(setActiveIcon('book-library'));
                } else {
                    const filteredItems: any[] = [];
                    for (const item of navList?.sideNavItems) {
                        if (item.key === 'school_management') {
                            for (const obj of item?.subItems) {
                                let productVal =
                                    product === 'Buildr'
                                        ? 'Buildr'
                                        : productType;
                                if (
                                    obj?.visibleToProducts?.includes(productVal)
                                ) {
                                    filteredItems.push(obj);
                                }
                            }
                        }
                    }
                    if (filteredItems?.find((item) => item.key === 'nucleus')) {
                        dispatch(setActiveLink('/nucleus'));
                        dispatch(setActiveIcon('nucleus'));
                    } else {
                        dispatch(setActiveLink('/book-library'));
                        dispatch(setActiveIcon('book-library'));
                    }
                }

                navigate(safeRedirectTo, { replace: true });
            }
        };

        window.addEventListener('message', handleLoginMessage);
        return () => window.removeEventListener('message', handleLoginMessage);
    }, [
        authLogin,
        dispatch,
        navigate,
        navList,
        product,
        productType,
        safeRedirectTo,
    ]);

    const handleIframeLoad = () => {
        iframeRef.current?.contentWindow?.postMessage(
            {
                type: 'REQUEST_LOCAL_STORAGE',
                logout: logout,
                pathUrl: location.pathname,
            },
            nucleusDomain
        );
    };

    useEffect(() => {
        const handlePopState = () => {
            navigate(1); // Moves forward to prevent back navigation
        };

        window.history.pushState(null, '', window.location.href);
        window.addEventListener('popstate', handlePopState);

        return () => {
            window.removeEventListener('popstate', handlePopState);
        };
    }, [navigate]);

    useEffect(() => {
        const handleMessage = (event: any) => {
            // Allow only specific origins

            if (event.data.type === 'LOGOUT') {
                localStorage.clear();
                sessionStorage.clear();
                navigate('/login', { replace: true });
            }
        };

        window.addEventListener('message', handleMessage);
        return () => window.removeEventListener('message', handleMessage);
    }, [navigate]);

    useEffect(() => {
        const checkAuthToken = () => {
            const authToken = localStorage.getItem('auth_token');
            if (!authToken && retryCount < 3) {
                console.warn(
                    `Retrying to fetch auth_token... Attempt ${retryCount + 1}`
                );
                handleIframeLoad();
                setRetryCount((prev) => prev + 1);
            }
        };

        if (!isLoading) {
            checkAuthToken();
        }
    }, [isLoading, retryCount]);

    useEffect(() => {
        let isLogout = logout;
    }, [logout]);

    let data = {
        invite: urlParams.get('invite') || '',
        teacherCode: urlParams.get('teacherCode') || '',
        toolbar: false,
        product: product,
        xClientApp: product,
        username: urlParams.get('username') || '',
        password: urlParams.get('password') || '',
        pathUrl: location.pathname + location.search,
    };

    let nucleusURL = `${process.env.REACT_APP_NUCLEUS_ACTIVE_TEACH_URL}/v2/auth/active-teach/login?emailData=${JSON.stringify(data)}`;
    return (
        <div className="w-screen h-screen fixed top-0 left-0">
            <div className="w-full h-full">
                {isLoading && (
                    <div className="absolute inset-0 flex items-center justify-center bg-white z-10">
                        <LoadingSpinner />
                    </div>
                )}
                <iframe
                    ref={iframeRef}
                    className="content-iframe w-full h-full transition-opacity duration-500 ease-in-out"
                    src={nucleusURL}
                    onLoad={handleLoad}
                    style={{
                        opacity: isLoading ? 0 : 1,
                        border: 'none', // Optional: Remove the iframe border
                    }}
                ></iframe>
            </div>
        </div>
    );
};

export default ActiveTeachLogin;
